/**
 * TaskReferenceView — React NodeView for the TaskReference inline node.
 *
 * Renders a clickable checkbox + task title as a real interactive element
 * instead of a CSS pseudo-element, making the checkbox clickable.
 */

import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { apiPatch } from '../../lib/api';

export function TaskReferenceView({ node, updateAttributes, extension }: NodeViewProps) {
  const { id, title, checked } = node.attrs;

  async function handleToggle(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();

    const newChecked = !checked;
    const newStatus = newChecked ? 'done' : 'todo';

    // Optimistic update
    updateAttributes({ checked: newChecked });

    try {
      await apiPatch(`/tasks/${id}`, { status: newStatus });
      extension.options.onTaskStatusChange?.(id, newStatus);
    } catch (err) {
      console.error('Failed to update task status:', err);
      // Revert on error
      updateAttributes({ checked: !newChecked });
    }
  }

  return (
    <NodeViewWrapper as="span" className={`task-reference-node ${checked ? 'task-reference-checked' : ''}`} data-checked={String(!!checked)}>
      <button
        type="button"
        className={`task-reference-checkbox ${checked ? 'task-reference-checkbox-checked' : ''}`}
        onClick={handleToggle}
        contentEditable={false}
        aria-label={checked ? 'Mark as incomplete' : 'Mark as complete'}
      />
      <span className={`task-reference-title ${checked ? 'task-reference-title-checked' : ''}`}>
        {title || 'Untitled task'}
      </span>
    </NodeViewWrapper>
  );
}
